import React, { useState, useEffect } from 'react';
import { camerasAPI } from '../lib/api';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from './ui/select';

import { Loader2, Trash2, Video, Plus, MapPin } from 'lucide-react';
import { Separator } from './ui/separator';
import { Badge } from './ui/badge';

export default function CameraManager({ projectId, siteId, isOpen, onClose, onCameraCreated }) {
  const [cameras, setCameras] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    camera_id: '',
    location: '',
    rtsp_url: '',
    status: 'active',
  });
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    if (isOpen && siteId) {
      loadCameras();
    }
  }, [isOpen, siteId]);

  const loadCameras = async () => {
    try {
      setLoading(true);
      const result = await camerasAPI.getAll(siteId);
      if (result.success) {
        setCameras(result.cameras || []);
      }
    } catch (error) {
      console.error('Error loading cameras:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsCreating(true);

    try {
      const result = await camerasAPI.create({
        ...formData,
        site_id: siteId,
        project_id: projectId,
      });

      if (result.success) {
        setFormData({
          name: '',
          camera_id: '',
          location: '',
          rtsp_url: '',
          status: 'active',
        });
        await loadCameras();
        if (onCameraCreated) {
          onCameraCreated(result.camera);
        }
      }
    } catch (err) {
      setError(err.message || 'Failed to create camera');
    } finally {
      setIsCreating(false);
    }
  };

  const handleDelete = async (cameraId) => {
    if (!window.confirm('Are you sure you want to delete this camera?')) {
      return;
    }

    try {
      await camerasAPI.delete(cameraId);
      await loadCameras();
    } catch (error) {
      setError(error.message || 'Failed to delete camera');
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Manage Cameras</DialogTitle>
          <DialogDescription>
            Add and configure cameras for this site.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-hidden flex flex-col gap-6">
          {/* Create Form */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium leading-none flex items-center gap-2">
              <Plus className="h-4 w-4" /> Add New Camera
            </h3>

            {error && (
              <div className="bg-destructive/10 text-destructive text-sm p-3 rounded-md">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-3">
              <div className="space-y-2">
                <Label htmlFor="camera-name">Camera Name <span className="text-destructive">*</span></Label>
                <Input
                  id="camera-name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g. Entrance Cam"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="camera-id">Camera ID <span className="text-destructive">*</span></Label>
                  <Input
                    id="camera-id"
                    value={formData.camera_id}
                    onChange={(e) => setFormData({ ...formData, camera_id: e.target.value })}
                    placeholder="cam-01"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="camera-location">Location</Label>
                  <Input
                    id="camera-location"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    placeholder="Lobby"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="camera-rtsp">RTSP URL</Label>
                <Input
                  id="camera-rtsp"
                  value={formData.rtsp_url}
                  onChange={(e) => setFormData({ ...formData, rtsp_url: e.target.value })}
                  placeholder="rtsp://example.com/stream"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="camera-status">Status</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value) => setFormData({ ...formData, status: value })}
                >
                  <SelectTrigger id="camera-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Button type="submit" className="w-full" disabled={isCreating}>
                {isCreating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {isCreating ? 'Creating...' : 'Create Camera'}
              </Button>
            </form>
          </div>

          <Separator />

          {/* Cameras List */}
          <div className="flex-1 overflow-hidden flex flex-col">
            <h3 className="text-sm font-medium leading-none mb-3">Existing Cameras</h3>
            <div className="flex-1 -mx-2 px-2 overflow-auto">
              {loading ? (
                <div className="flex items-center justify-center py-8 text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Loading...
                </div>
              ) : cameras.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground text-sm border rounded-lg border-dashed">
                  No cameras created yet
                </div>
              ) : (
                <div className="space-y-2 pb-4">
                  {cameras.map((camera) => (
                    <div
                      key={camera.id}
                      className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-accent/10 transition-colors"
                    >
                      <div className="space-y-1 overflow-hidden">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium leading-none truncate">{camera.name}</p>
                          <Badge
                            variant="secondary"
                            className={`text-[10px] h-4 px-1.5 ${camera.status === 'active'
                              ? 'bg-green-500/10 text-green-700 hover:bg-green-500/20'
                              : 'bg-red-500/10 text-red-700 hover:bg-red-500/20'
                              }`}
                          >
                            {camera.status}
                          </Badge>
                        </div>
                        <div className="flex items-center text-xs text-muted-foreground gap-2">
                          <div className="flex items-center truncate">
                            <Video className="h-3 w-3 mr-1 shrink-0" />
                            <span className="truncate">{camera.camera_id}</span>
                          </div>
                          {camera.location && (
                            <div className="flex items-center truncate border-l pl-2">
                              <MapPin className="h-3 w-3 mr-1 shrink-0" />
                              <span className="truncate">{camera.location}</span>
                            </div>
                          )}
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-muted-foreground hover:text-destructive shrink-0"
                        onClick={() => handleDelete(camera.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                        <span className="sr-only">Delete</span>
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
